import { useFrame, useThree } from "@react-three/fiber";
import { Sky, useScroll } from "@react-three/drei";
import { Sparkles } from "../../Sparkles";
import * as React from "react";
import * as THREE from "three";
import { random } from "maath";
import { Cloud } from "../../Cloud";
import { Clouds } from "../../Clouds";
import { makeSphericalDistribute } from "../utils";
import {
  DEFAULT_FOG_COLOR,
  DEFAULT_FOG_FAR,
  DEFAULT_FOG_NEAR,
  HERO_SKY_DISTANCE,
  RADIO_SKY_SUN_POSITION,
  SERVICES_FOG_COLOR,
} from "../constants";
import { PageTransitionContext } from "../PageTransitionContext";
import SpiritBoxRadio from "../../SpiritBoxRadio";
import { useBreakpoint, Breakpoint } from "../../../hooks/useBreakpoint";
import { radioScrollLayout, useScrollLayout } from "../scrollLayout";
import {
  deriveRadioWindows,
  deriveRadioPath,
  TRAVEL_CROSS_START,
  DEFAULT_RADIO_MEASUREMENT,
} from "./radioScrollWindows";

/* ── Camera ── */
const CAM_POS = new THREE.Vector3(0, 0, 12);
const CAM_TARGET = new THREE.Vector3(0, 0, 0);

/* ── Clouds ── */
const CLOUDS_POS: [number, number, number] = [0, 0, -6];

// Baked cloud values, seeded from the /decks page look (DECKS_CARDS_CLOUDS).
// The z* distribution bias keeps puffs behind the radio model; scrollPanZ
// flies the field toward the fixed camera across the scroll.
const RADIO_CLOUDS = {
  color: "#ffffff",
  opacity: 0.6,
  speed: 0.13,
  scale: 1.05,
  volume: 257,
  growth: 200,
  smallestVolume: 0.5,
  bounds: 401,
  seed: 2,
  yMul: 0.8,
  yAdd: 0.15,
  // Full shell: the camera now ORBITS the device (including behind it), so
  // the puffs surround the stage in every direction at ~bounds distance —
  // no bare-sky horizon band when the camera swings around, and the shell
  // (~400 away) can never wander between the orbit (≤ 12.5) and the device.
  zMul: 1,
  zAdd: 0,
  // Gentle drift only — the orbiting camera supplies the real parallax.
  scrollPanZ: 40,
} as const;

/* ── Model placement ──
   Desktop/tablet: hero copy sits in the left column, the radio floats on the
   right half; the order-section appearance mirrors it on the far left so the
   centered order copy stays clear. Mobile: the model rides the top third
   above the stacked text (decks pattern). Reasoned starting values — tune
   live. */
type RadioLayout = {
  hero: { x: number; y: number; fit: number };
  order: { x: number; y: number; fit: number };
  camZ: number;
};
const RADIO_LAYOUT_BY_BP: Record<Breakpoint, RadioLayout> = {
  desktop: {
    hero: { x: 3.4, y: -0.2, fit: 5 },
    order: { x: -4.3, y: 0, fit: 5 },
    camZ: 12,
  },
  tablet: {
    hero: { x: 3.0, y: -0.2, fit: 4.4 },
    order: { x: -3.8, y: 0, fit: 4.4 },
    camZ: 12,
  },
  mobile: {
    hero: { x: 0, y: 8.3, fit: 4 },
    // Same top band as the hero: the order copy starts ~34vh down, so the
    // model must sit fully above the title (y 3.1 had it behind the text).
    order: { x: 0, y: 8.3, fit: 2.6 },
    camZ: 18,
  },
};

/* ── Mobile cloud liveliness ──
   The tablet/mobile tour has no traveling model (that's a desktop, camera-
   driven affair), so the sense of motion comes from the sky instead. The
   dominant move is flying THROUGH the field: the puffs sit on a ~400-unit
   shell, so the pan must carry the whole front hemisphere past the camera
   over the tour (~420 units) — anything smaller never crosses a single
   puff and reads as a still sky. Yaw + sink stay as secondary cues. */
const MOBILE_CLOUD_TURN = 1.1;
const MOBILE_CLOUD_PAN_Z = 420;
const MOBILE_CLOUD_SINK_Y = -14;

/* ── Per-section CAMERA views (desktop showcase) ──
   The motion paradigm is camera-driven (like the home page): the device
   floats fixed at the origin and the CAMERA travels around it — orbiting,
   craning over the top edge, diving in for the screen, swinging behind for
   the battery. The cloud field gets true parallax with every move.

   theta: azimuth around Y (0 = in front, π = behind), phi: elevation,
   dist: orbit radius, tx: lookAt offset along X — the camera frames a point
   beside the device, so the device sits on the LEFT half (tx > 0) while the
   copy owns the right column; the hero flips it (tx < 0 → device right).
   Reasoned starting values — tune live. */
type CamView = { theta: number; phi: number; dist: number; tx: number };
const D2R = Math.PI / 180;
const TOUR_TX = 3.9;
// Global orbit-radius multiplier: pulls the camera uniformly closer so the
// device reads bigger in every section. Scaling dist AND tx together keeps the
// composition intact — apparent size and the off-centre framing both scale by
// 1/DIST_SCALE, so the device stays left and the copy keeps the right column.
const DIST_SCALE = 0.85;
const camView = (
  thetaDeg: number,
  phiDeg: number,
  dist: number,
  tx: number = TOUR_TX,
): CamView => ({
  theta: thetaDeg * D2R,
  phi: phiDeg * D2R,
  dist: dist * DIST_SCALE,
  tx: tx * DIST_SCALE,
});

// Hero pulls in closer than the tour (smaller dist) so the device reads big on
// the landing screen; tx eased in step with dist (ratio ≈ unchanged) keeps it
// at the same horizontal spot instead of drifting off the right edge.
const HERO_CAM: CamView = camView(16, 2, 10.5, -3.4);
const ORDER_CAM: CamView = camView(-14, 3, 11.5);

/* ── Control highlight markers ──
   The Meshy model has NO labelled buttons — the keypad is a grid of identical
   unlabelled ovals, so "the ANTENNA button" doesn't exist as a distinct
   element. We therefore ring the DISTINCT, recognisable features that DO
   exist and that each section is really about. Positions measured from an
   orthographic probe render (fit space, height 5 centred at origin):
     screen  [0.03, 1.45]   wing speaker [0, -1.2]   keypad block [0, 0.45]
     antenna nub [-0.7, 2.55]   top edge [0, 2.35]   back cover [0, 0.3, z-]
   `r` scales the ring to roughly encircle the feature. The ring lives inside
   the model group (tracks every turn/zoom); depth testing hides it when the
   feature faces away. */
type ControlMarker = {
  p: [number, number, number];
  face: "front" | "back" | "top";
  /** Ring radius scale to bound the feature. */
  r?: number;
};
/** Master switch for the control-highlight rings (+ their spotlight). Off for
 *  now — flip to true to bring the feature highlights back. */
const HIGHLIGHTS_ENABLED = false;

const SCREEN: ControlMarker = { p: [0.03, 1.45, 0.4], face: "front", r: 3.1 };
const WING: ControlMarker = { p: [0, -1.2, 0.38], face: "front", r: 4.2 };
const KEYPAD: ControlMarker = { p: [0, 0.45, 0.4], face: "front", r: 3.7 };
// Indexed by section position (s1 → 0, …) for the CONSOLIDATED 16-section
// order: what / no-AI / design / antenna / sweep / noise / lock / temperature
// / backlight / battery / connectivity / specs / how-to / who-for / included
// / notice. (Highlights are gated off via HIGHLIGHTS_ENABLED — kept aligned
// so re-enabling Just Works.)
const CONTROL_MARKERS: (ControlMarker | null)[] = [
  null, // what
  null, // no AI
  WING, // design — exclusive angel-wing speaker
  { p: [-0.7, 2.55, 0.12], face: "front", r: 0.8 }, // antenna — the antenna nub
  SCREEN, // sweep — band/speed/direction shown on the display
  WING, // noise — audio shaped at the speaker
  KEYPAD, // lock — protects the front keypad
  SCREEN, // temperature — read on the display
  SCREEN, // backlight — the display
  { p: [0, 0.3, -0.4], face: "back", r: 3.4 }, // battery — removable back cover
  { p: [0.32, 2.35, 0], face: "top", r: 0.95 }, // connectivity — USB-C/AUX, top edge
  null, // specs
  null, // how-to
  null, // who-for
  null, // included
  null, // notice
];

const MARKER_FACE_ROTATION: Record<ControlMarker["face"], [number, number, number]> = {
  front: [0, 0, 0],
  back: [0, Math.PI, 0],
  // Tilted toward the camera (not flat on the edge) — a flat ring shows as a
  // sliver at the TOP view's rx and becomes unreadable.
  top: [-Math.PI / 2 + 0.7, 0, 0],
};

/* ── Living screen ──
   The GLB's emissive texture is solid black, so the "powered-on" look is a
   soft static backlight quad over the LCD ([0.03, 1.42], ≈1.5×0.6 in fit
   units). No moving sweep line — it read as a distracting running glitch. */
const SCREEN_GLOW_GEOMETRY = new THREE.PlaneGeometry(1.5, 0.58);
const SCREEN_GLOW_MATERIAL = new THREE.MeshBasicMaterial({
  color: "#a8d4f5",
  transparent: true,
  opacity: 0.28,
  depthWrite: false,
});

/* ── Dusk atmosphere ──
   The whole /spirit-box page now holds the brand's dusk-blue (the home services
   scene color) — every section runs white-on-dusk type, so the backdrop must
   stay reliably dark from hero to FAQ. Desktop only: the tablet/mobile flow
   keeps dark-on-light type. */
const FOG_LIGHT = new THREE.Color(DEFAULT_FOG_COLOR);
const FOG_DUSK = new THREE.Color(SERVICES_FOG_COLOR);
// The cloud layer covers the whole backdrop, so the dusk must tint the puffs
// themselves (the shared instanced material multiplies per-instance colors).
// Deep tint + lower opacity: the puffs read as dark atmospheric texture, not a
// pale bank — a mid-grey #7c93ae cloud bank washed out behind the white FAQ
// copy at the depth where the puffs stack densest. This keeps every cloud
// patch dark enough to carry white type.
const CLOUD_LIGHT = new THREE.Color("#ffffff");
const CLOUD_DUSK = new THREE.Color("#414f66");
const CLOUD_OPACITY_LIGHT = 0.6;
const CLOUD_OPACITY_DUSK = 0.28;

const _markerWorld = new THREE.Vector3();
const _spotDir = new THREE.Vector3();
const _camLook = new THREE.Vector3();
const _camAnchor = new THREE.Vector3();
const _camFwd = new THREE.Vector3();
const _camRight = new THREE.Vector3();
const _worldUp = new THREE.Vector3(0, 1, 0);

const MARKER_RING_GEOMETRY = new THREE.RingGeometry(0.17, 0.22, 40);
const MARKER_RING_MATERIAL = new THREE.MeshBasicMaterial({
  color: "#36628e",
  transparent: true,
  opacity: 0,
  depthWrite: false,
  side: THREE.DoubleSide,
});
const MARKER_FILL_GEOMETRY = new THREE.CircleGeometry(0.17, 40);
const MARKER_FILL_MATERIAL = new THREE.MeshBasicMaterial({
  color: "#5b87b5",
  transparent: true,
  opacity: 0,
  depthWrite: false,
  side: THREE.DoubleSide,
});
/** Indexed by section position (s1 → 0, …): what / no-AI / design / antenna
 *  / sweep / speed / direction / noise / lock / temperature / backlight /
 *  battery / charging / aux / no-bluetooth / specs / how-to / who-for /
 *  included / notice. */
const SECTION_CAMS: CamView[] = [
  camView(-30, 6, 11), // what — wide 3/4 introduction
  camView(-6, 0, 10), // no AI — honest, straight on
  camView(-20, -6, 9), // design — front, slightly low to face the wing
  camView(-16, 16, 9), // antenna — front + slight top to see the antenna nub
  camView(-6, 4, 9), // sweep — front, faces the screen
  camView(8, -6, 9), // noise — front, slightly low for the wing
  camView(20, 0, 8.5), // lock — front 3/4, faces the keypad
  camView(2, 3, 7.8, 4.6), // temperature — close, faces the screen
  camView(-8, 2, 7.8, 4.6), // backlight — close, faces the screen
  camView(150, 6, 9.5), // battery — swing behind the device
  camView(22, 45, 8.5), // connectivity — over the top, USB-C / AUX
  camView(-38, 8, 11.5), // specs — wide
  camView(12, 4, 10.5), // how-to
  camView(-26, 0, 10.5), // who-for
  camView(6, 0, 9.5), // included
  camView(-10, 4, 11), // notice
];

function clamp01(x: number) {
  return Math.max(0, Math.min(1, x));
}

function easeOutQuart(t: number) {
  return 1 - Math.pow(1 - t, 4);
}

type Props = {
  /** True once exit transition is requested; freezes scroll-driven scaling so
   *  the model doesn't keep animating under the FBO snapshot while React
   *  waits for the next page's assets to commit. */
  isExiting?: boolean;
};

export function RadioPart({ isExiting }: Props) {
  const scroll = useScroll();
  const { camera } = useThree();
  const breakpoint = useBreakpoint();
  const layout = RADIO_LAYOUT_BY_BP[breakpoint];
  // Desktop: the model travels beside the centered copy column, alternating
  // margins section by section. Tablet/mobile have no side margins, so they
  // keep the two-appearance choreography (hero + order).
  const travels = breakpoint === "desktop";

  // Scroll windows derived from the measured page layout (see
  // radioScrollWindows.ts). Re-renders on re-measure; the useFrame closure
  // below always reads the latest value.
  const radioLayout = useScrollLayout(radioScrollLayout);
  const W = React.useMemo(
    () =>
      (radioLayout && deriveRadioWindows(radioLayout)) ??
      deriveRadioWindows(DEFAULT_RADIO_MEASUREMENT)!,
    [radioLayout],
  );
  const path = React.useMemo(
    () =>
      (radioLayout && deriveRadioPath(radioLayout)) ??
      deriveRadioPath(DEFAULT_RADIO_MEASUREMENT)!,
    [radioLayout],
  );

  // Camera timeline: [hero, s1…sN, order]; the active camera view switches
  // at each leg's take-off point (shared TRAVEL_CROSS_START), so the swing
  // happens exactly while the outgoing copy fades and settles by the next
  // landing. The device itself never travels — it floats at the origin.
  const viewTimeline = React.useMemo(() => {
    const stopsT = [0, ...path.sections, path.order];
    const views = [
      HERO_CAM,
      ...path.sections.map((_, i) => SECTION_CAMS[i] ?? SECTION_CAMS[1]),
      ORDER_CAM,
    ];
    return { stopsT, views };
  }, [path]);

  // Respect prefers-reduced-motion: keep the crossfades, drop the idle
  // float/sway/parallax so the model holds still between view changes.
  const reducedMotion = React.useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  /* Cloud distribution */
  const spherical = React.useMemo(() => {
    const box = random.inBox(new Float32Array(100 * 3), { sides: [1, 1, 1] });
    return random.onSphere(box, { radius: 1 }) as Float32Array;
  }, []);
  const segments = spherical.length / 3;

  const cloud = RADIO_CLOUDS;

  /* Refs */
  const cloudsRef = React.useRef<THREE.Group>(null);
  const radioRef = React.useRef<THREE.Group>(null);
  const radioScale = React.useRef(0);
  const markerRef = React.useRef<THREE.Group>(null);
  /* Marker fade state: which control is lit and how far it has faded in. */
  const marker = React.useRef({ opacity: 0, idx: -1 });
  const fogRef = React.useRef<THREE.Fog>(null);
  /* drei <Sky> mesh — its shader ignores fog, so the dusk must move the SUN:
   * with the camera orbiting, the sun-side sky otherwise blows out exactly
   * behind the white copy. */
  const skyRef = React.useRef<React.ElementRef<typeof Sky> | null>(null);
  /* Shared instanced cloud material, resolved lazily after Clouds mounts. */
  const cloudMat = React.useRef<THREE.MeshLambertMaterial | null>(null);
  /* Highlight spotlight + the global lights it borrows attention from. */
  const spotRef = React.useRef<THREE.SpotLight>(null);
  const spotTargetRef = React.useRef<THREE.Object3D>(null);
  const ambientRef = React.useRef<THREE.AmbientLight>(null);
  const hemiRef = React.useRef<THREE.HemisphereLight>(null);
  const dirRef = React.useRef<THREE.DirectionalLight>(null);

  React.useEffect(() => {
    if (spotRef.current && spotTargetRef.current) {
      spotRef.current.target = spotTargetRef.current;
    }
  }, []);
  /* Dampened pointer for a subtle "look at the cursor" parallax tilt. */
  const ptr = React.useRef({ x: 0, y: 0 });
  /* Dampened pose for the tablet/mobile object motion (null until the first
   * frame seeds it). */
  const pose = React.useRef<{
    x: number;
    y: number;
    spin: number;
  } | null>(null);
  /* Dampened camera spherical state for the desktop orbit (null until the
   * first frame seeds it from the hero view). */
  const camPose = React.useRef<CamView | null>(null);

  /* Camera init */
  React.useEffect(() => {
    camera.position.set(CAM_POS.x, CAM_POS.y, layout.camZ);
    (camera as THREE.PerspectiveCamera).lookAt(CAM_TARGET);
    camera.updateMatrixWorld();
  }, [camera, layout.camZ]);

  const isExitingRef = React.useRef(false);
  isExitingRef.current = !!isExiting;
  const { isTransitioningRef } = React.useContext(PageTransitionContext);

  useFrame((state, delta) => {
    const offset = scroll.offset;
    const elapsed = state.clock.elapsedTime;

    // ── Dusk atmosphere ──
    // /spirit-box is fully nocturnal on every breakpoint: the dark dusk backdrop
    // holds across the whole page (one palette for all resolutions).
    const duskK = 1;
    if (fogRef.current) {
      fogRef.current.color.lerpColors(FOG_LIGHT, FOG_DUSK, duskK);
      // Pull the fog in a touch in the dusk so the depth feels denser.
      fogRef.current.far = DEFAULT_FOG_FAR - 220 * duskK;
    }
    if (!cloudMat.current && cloudsRef.current) {
      cloudsRef.current.traverse((o) => {
        const inst = o as THREE.InstancedMesh;
        if (inst.isInstancedMesh) {
          cloudMat.current = inst.material as THREE.MeshLambertMaterial;
        }
      });
    }
    if (cloudMat.current) {
      cloudMat.current.color.lerpColors(CLOUD_LIGHT, CLOUD_DUSK, duskK);
      cloudMat.current.opacity =
        CLOUD_OPACITY_LIGHT + (CLOUD_OPACITY_DUSK - CLOUD_OPACITY_LIGHT) * duskK;
    }
    if (dirRef.current) {
      // The key light is what blows the puffs out behind the white copy.
      dirRef.current.intensity = 1.6 - 0.7 * duskK;
    }
    if (skyRef.current) {
      // Sink the sun below the horizon as the dusk sets in — the Sky shader
      // darkens physically (it ignores fog, so this is the only way to kill
      // the blown sun-side patch behind the text).
      const skyMesh = skyRef.current as unknown as THREE.Mesh;
      const sun = (skyMesh.material as THREE.ShaderMaterial).uniforms
        ?.sunPosition?.value as THREE.Vector3 | undefined;
      sun?.set(
        RADIO_SKY_SUN_POSITION[0],
        // Sink the sun DEEP below the horizon at full dusk (1.8 − 6 = −4.2).
        // A sun just below the horizon (−0.5) left a bright twilight glow band
        // that the orbiting camera faced on some sections (the order view washed
        // out white); pushing it well under kills the horizon glow so the Sky
        // stays uniformly dark on every camera angle.
        RADIO_SKY_SUN_POSITION[1] - 6 * duskK,
        RADIO_SKY_SUN_POSITION[2],
      );
    }

    let vis: number;
    let x: number;
    let y: number;
    let fitFactor = 1;
    let scrollSpin: number;

    let camTarget: CamView | null = null;
    let markerIdx = -1;

    if (travels) {
      // ── Desktop: camera-driven showcase. The device floats at the origin;
      //    the camera orbits to each section's view and the model fades out
      //    before the FAQ tops the viewport. ──
      vis =
        1 -
        easeOutQuart(
          clamp01(
            (offset - W.order.exitStart) / (W.order.exitEnd - W.order.exitStart),
          ),
        );

      x = 0;
      y = -0.2;
      scrollSpin = 0;

      // Active camera view: switch at each leg's take-off so the swing
      // overlaps the text crossfade (same timing source as
      // deriveRadioTextWindows).
      const { stopsT, views } = viewTimeline;
      let k = 0;
      while (k < stopsT.length - 1) {
        const takeoff =
          stopsT[k] + TRAVEL_CROSS_START * (stopsT[k + 1] - stopsT[k]);
        if (offset > takeoff) k++;
        else break;
      }
      camTarget = views[Math.min(k, views.length - 1)];
      // views[0] is the hero, views[1..N] map to sections s1..sN.
      markerIdx = k - 1;
    } else {
      // ── Tablet/mobile: two appearances (hero + order). The windows never
      //    overlap (~10 screens apart), so the position can snap to whichever
      //    appearance is active while the scale is faded out. ──
      const heroVis =
        1 -
        easeOutQuart(
          clamp01(
            (offset - W.hero.exitStart) / (W.hero.exitEnd - W.hero.exitStart),
          ),
        );
      const orderVis =
        easeOutQuart(
          clamp01(
            (offset - W.order.enterStart) /
              (W.order.enterEnd - W.order.enterStart),
          ),
        ) *
        (1 -
          easeOutQuart(
            clamp01(
              (offset - W.order.exitStart) /
                (W.order.exitEnd - W.order.exitStart),
            ),
          ));
      vis = Math.max(heroVis, orderVis);
      const place = orderVis > heroVis ? layout.order : layout.hero;
      x = place.x;
      y = place.y;
      fitFactor = place.fit / layout.hero.fit;
      // Leaving the hero spins the device half a turn, so the exit reads as
      // a deliberate reveal rather than a plain fade.
      scrollSpin = clamp01(offset / Math.max(0.001, W.hero.exitEnd)) * Math.PI;
    }

    radioScale.current = THREE.MathUtils.damp(radioScale.current, vis, 10, delta);

    // Dampened pointer parallax — the device leans toward the cursor.
    ptr.current.x = THREE.MathUtils.damp(ptr.current.x, state.pointer.x, 3, delta);
    ptr.current.y = THREE.MathUtils.damp(ptr.current.y, state.pointer.y, 3, delta);

    // motion gate: prefers-reduced-motion keeps the device still and the
    // camera steady between view changes (crossfades and the meaningful
    // camera swings remain).
    const motion = reducedMotion ? 0 : 1;

    // Inertia. Tablet/mobile object pose snaps (the two appearances are far
    // apart and the model is invisible in between); the desktop CAMERA damps
    // toward the active view — λ≈1.9 reads as a deliberate crane move and
    // absorbs wheel steps. Seeded on the first frame.
    if (pose.current == null) pose.current = { x, y, spin: scrollSpin };
    const pc = pose.current;
    if (!travels) {
      pc.x = x;
      pc.y = y;
      pc.spin = scrollSpin;
    }
    if (travels && camTarget) {
      if (camPose.current == null) camPose.current = { ...camTarget };
      const cp = camPose.current;
      cp.theta = THREE.MathUtils.damp(cp.theta, camTarget.theta, 1.9, delta);
      cp.phi = THREE.MathUtils.damp(cp.phi, camTarget.phi, 1.9, delta);
      cp.dist = THREE.MathUtils.damp(cp.dist, camTarget.dist, 1.9, delta);
      cp.tx = THREE.MathUtils.damp(cp.tx, camTarget.tx, 1.9, delta);
    }

    if (radioRef.current) {
      const s = radioScale.current;
      const float = Math.sin(elapsed * 0.8) * 0.15 * motion;
      const rock = Math.sin(elapsed * 0.5) * 0.06 * motion;
      const sway = Math.sin(elapsed * 0.3) * 0.05 * motion;
      if (travels) {
        // The device holds the stage center; all the travel is the camera's.
        radioRef.current.position.set(0, -0.2 + float * s, 0);
        radioRef.current.rotation.set(0, sway, rock * s * 0.4);
        radioRef.current.scale.setScalar(s);
      } else {
        radioRef.current.position.set(pc.x, pc.y + float * s, 0);
        radioRef.current.rotation.set(
          ptr.current.y * -0.12 * motion,
          elapsed * 0.25 * motion + pc.spin + ptr.current.x * 0.3 * motion,
          rock * s * 0.5,
        );
        radioRef.current.scale.setScalar(s * fitFactor);
      }
    }

    // ── Control highlight: a pulsing dot on the control the section talks
    //    about. Fades out before repositioning so it never slides across the
    //    device body; lives inside the model group so it tracks every turn. ──
    if (markerRef.current) {
      const m = marker.current;
      const target =
        HIGHLIGHTS_ENABLED && travels && markerIdx >= 0 && markerIdx < CONTROL_MARKERS.length
          ? CONTROL_MARKERS[markerIdx]
          : null;
      // Reposition only while invisible.
      if (target && m.opacity < 0.03) {
        markerRef.current.position.set(target.p[0], target.p[1], target.p[2]);
        const rot = MARKER_FACE_ROTATION[target.face];
        markerRef.current.rotation.set(rot[0], rot[1], rot[2]);
        m.idx = markerIdx;
      }
      const switching = target != null && m.idx !== markerIdx;
      const want = target && !switching && vis > 0.5 ? 1 : 0;
      m.opacity = THREE.MathUtils.damp(m.opacity, want, 5, delta);
      const pulse = reducedMotion ? 1 : 0.78 + 0.22 * Math.sin(elapsed * 2.8);
      MARKER_RING_MATERIAL.opacity = m.opacity * 0.95 * pulse;
      MARKER_FILL_MATERIAL.opacity = m.opacity * 0.16 * pulse;
      const r = (target?.r ?? 1) * (1 + (reducedMotion ? 0 : 0.08 * Math.sin(elapsed * 2.8)));
      markerRef.current.scale.setScalar(r);
      markerRef.current.visible = m.opacity > 0.01;

      // ── Highlight light: a cool spotlight pools on the marked control
      //    while the ambient/hemisphere dim a touch — the rest of the white
      //    body recedes and the feature visibly lights up. ──
      if (spotRef.current && spotTargetRef.current) {
        markerRef.current.getWorldPosition(_markerWorld);
        spotTargetRef.current.position.copy(_markerWorld);
        // Shine from the camera's side of the control (the camera may be
        // behind/above the device now), lifted a little for modelling.
        _spotDir.copy(camera.position).sub(_markerWorld).normalize();
        spotRef.current.position
          .copy(_markerWorld)
          .addScaledVector(_spotDir, 4.5);
        spotRef.current.position.y += 1.2;
        spotRef.current.intensity = m.opacity * (2.4 + 0.9 * pulse);
      }
      if (ambientRef.current) {
        ambientRef.current.intensity = 1.4 - 0.2 * duskK - 0.45 * m.opacity;
      }
      if (hemiRef.current) {
        hemiRef.current.intensity = 1.6 - 0.25 * duskK - 0.55 * m.opacity;
      }
    }


    // ── Cloud field. Desktop: a gentle drift — the orbiting camera supplies
    //    the real parallax. Tablet/mobile: the camera is fixed and the model
    //    is gone for most of the tour, so the sky carries the motion — a much
    //    deeper toward-the-camera pan plus a slow sink per scroll step. ──
    if (cloudsRef.current) {
      cloudsRef.current.position.set(
        CLOUDS_POS[0],
        CLOUDS_POS[1] + (travels ? 0 : offset * MOBILE_CLOUD_SINK_Y),
        CLOUDS_POS[2] +
          offset * (travels ? cloud.scrollPanZ : MOBILE_CLOUD_PAN_Z),
      );
      cloudsRef.current.rotation.y = travels ? 0 : offset * MOBILE_CLOUD_TURN;
    }

    /* Camera. Desktop: damped orbit around the device (spherical → cartesian)
       with pointer parallax on the eye; tablet/mobile keep the fixed camera. */
    if (travels && camPose.current) {
      const cp = camPose.current;
      const cosP = Math.cos(cp.phi);
      camera.position.set(
        Math.sin(cp.theta) * cosP * cp.dist + ptr.current.x * 0.5 * motion,
        Math.sin(cp.phi) * cp.dist + 0.2 + ptr.current.y * 0.35 * motion,
        Math.cos(cp.theta) * cosP * cp.dist,
      );
      // Frame the device off to one side by shifting the look-target along
      // the camera's RIGHT vector (NOT world X). World-X framing flips
      // handedness once the orbit passes 90° (battery view, θ=150°) and the
      // device jumps onto the text. Screen-relative offset keeps it left at
      // every angle: target = devicePoint + right·tx → device sits tx to the
      // left of center.
      _camAnchor.set(0, 0.2, 0);
      _camFwd.copy(_camAnchor).sub(camera.position).normalize();
      _camRight.crossVectors(_camFwd, _worldUp).normalize();
      _camLook.copy(_camAnchor).addScaledVector(_camRight, cp.tx);
      camera.lookAt(_camLook);
    } else {
      camera.position.set(CAM_POS.x, CAM_POS.y, layout.camZ);
      camera.lookAt(CAM_TARGET);
    }

    // Hide the model while a page transition is in flight so the user doesn't
    // see it scale-ramping under the fading snapshot.
    const hide = isExitingRef.current || isTransitioningRef.current;
    if (radioRef.current) {
      radioRef.current.visible = !hide && radioScale.current > 0.01;
    }
  });

  return (
    <group>
      {/* ── Atmosphere ── */}
      <Sky
        ref={skyRef}
        distance={HERO_SKY_DISTANCE}
        sunPosition={RADIO_SKY_SUN_POSITION}
      />
      <fog
        ref={fogRef}
        attach="fog"
        args={[DEFAULT_FOG_COLOR, DEFAULT_FOG_NEAR, DEFAULT_FOG_FAR]}
      />

      {/* Lighting */}
      <ambientLight ref={ambientRef} intensity={1.4} />
      <hemisphereLight ref={hemiRef} color="#ffffff" groundColor="#aab2bc" intensity={1.6} />
      <directionalLight ref={dirRef} intensity={1.6} position={[50, 200, 100]} />
      {/* Control-highlight spotlight (driven per-frame; off when no marker). */}
      <spotLight
        ref={spotRef}
        color="#eaf3ff"
        intensity={0}
        angle={0.32}
        penumbra={0.75}
        distance={30}
        decay={1.2}
      />
      <object3D ref={spotTargetRef} />
      <pointLight position={[0, -1, 8]} intensity={2} color="#ffffff" />
      <pointLight
        position={[-6, -3, 4]}
        intensity={1.2}
        color="#e0e8f5"
        distance={20}
        decay={1.5}
      />
      <pointLight
        position={[6, -3, 4]}
        intensity={1.2}
        color="#e0e8f5"
        distance={20}
        decay={1.5}
      />

      {/* ── Clouds ── */}
      <Clouds ref={cloudsRef} scale={cloud.scale} limit={segments} position={CLOUDS_POS}>
        <Cloud
          segments={segments}
          seed={cloud.seed}
          volume={cloud.volume}
          growth={cloud.growth}
          smallestVolume={cloud.smallestVolume}
          bounds={cloud.bounds}
          opacity={cloud.opacity}
          speed={cloud.speed}
          color={cloud.color}
          concentrate="random"
          distribute={makeSphericalDistribute(spherical, {
            yMul: cloud.yMul,
            yAdd: cloud.yAdd,
            // Bias all puffs behind the radio model (world z ≤ -6) — same
            // depthWrite=false + back-to-front sorting caveat as /decks.
            zMul: cloud.zMul,
            zAdd: cloud.zAdd,
          })}
        />
      </Clouds>

      {/* Sparkles — denser/brighter than the decks scene; they carry the
          mystical read once the dusk zone darkens the backdrop. */}
      <Sparkles
        count={70}
        scale={[20, 12, 10]}
        size={2.2}
        speed={0.25}
        opacity={0.5}
        color="#dce6ff"
      />

      {/* ── Spirit box model ── */}
      <group ref={radioRef} scale={0}>
        <SpiritBoxRadio fit={layout.hero.fit} />
        {/* Living screen: static LCD backlight. */}
        <group position={[0.03, 1.42, 0.41]}>
          <mesh geometry={SCREEN_GLOW_GEOMETRY} material={SCREEN_GLOW_MATERIAL} />
        </group>
        {/* Control highlight (ring + faint fill) — positioned per section. */}
        <group ref={markerRef} visible={false}>
          <mesh geometry={MARKER_RING_GEOMETRY} material={MARKER_RING_MATERIAL} />
          <mesh geometry={MARKER_FILL_GEOMETRY} material={MARKER_FILL_MATERIAL} />
        </group>
      </group>
    </group>
  );
}
